import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Paper,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { documentationApi } from '../api/documentation';
import { Documentation, DocumentationItem, DocumentationItemType } from '../types/documentation';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import RichTextEditor from '../components/RichTextEditor';

const DocumentationView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [documentation, setDocumentation] = useState<Documentation | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState<DocumentationItem | null>(null);
    const [newItemType, setNewItemType] = useState<DocumentationItemType>(DocumentationItemType.TEXT);
    const [newItemContent, setNewItemContent] = useState('');
    const [newItemDescription, setNewItemDescription] = useState('');
    const [newItemTitle, setNewItemTitle] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (id) {
            loadDocumentation();
        }
    }, [id]);

    // Cleanup preview URL when component unmounts
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const loadDocumentation = async () => {
        try {
            const response = await documentationApi.getDocumentation(Number(id));
            setDocumentation(response.data);
        } catch (error) {
            console.error('Error loading documentation:', error);
        }
    };

    const handleCreateItem = async () => {
        if (!documentation) return;

        try {
            if ((newItemType === DocumentationItemType.FILE || newItemType === DocumentationItemType.IMAGE) && selectedFile) {
                await documentationApi.uploadFile(documentation.id, selectedFile, newItemDescription, newItemTitle);
            } else {
                await documentationApi.createDocumentationItem(documentation.id, {
                    type: newItemType,
                    content: newItemContent,
                    description: newItemDescription,
                    title: newItemTitle,
                });
            }
            setOpenDialog(false);
            resetForm();
            loadDocumentation();
        } catch (error) {
            console.error('Error creating item:', error);
        }
    };

    const handleUpdateItem = async () => {
        if (!editingItem || !documentation) return;

        try {
            await documentationApi.updateDocumentationItem(documentation.id, editingItem.id, {
                content: newItemContent,
                description: newItemDescription,
                title: newItemTitle,
            });
            setOpenDialog(false);
            resetForm();
            loadDocumentation();
        } catch (error) {
            console.error('Error updating item:', error);
        }
    };

    const handleDeleteItem = async (itemId: number) => {
        if (!documentation) return;
        
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                await documentationApi.deleteDocumentationItem(documentation.id, itemId);
                loadDocumentation();
            } catch (error) {
                console.error('Error deleting item:', error);
            }
        }
    };

    const resetForm = () => {
        setNewItemType(DocumentationItemType.TEXT);
        setNewItemContent('');
        setNewItemDescription('');
        setNewItemTitle('');
        setSelectedFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setEditingItem(null);
    };

    const loadImage = async (filePath: string) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            // Нормализуем путь к файлу
            const normalizedPath = filePath.replace(/\\/g, '/');
            const response = await fetch(`/api/v1/files/${normalizedPath}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to load image');
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageUrls(prev => ({
                ...prev,
                [filePath]: objectUrl
            }));
        } catch (error) {
            console.error('Error loading image:', error);
        }
    };

    const getFileUrl = (filePath: string | null) => {
        if (!filePath) return '';
        // Нормализуем путь к файлу
        const normalizedPath = filePath.replace(/\\/g, '/');
        return `/api/v1/files/${normalizedPath}`;
    };

    const handleFileClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string, fileName?: string) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.blob();
        })
        .then(blob => {
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            if (fileName) {
                link.setAttribute('download', fileName);
            }
            link.click();
            window.URL.revokeObjectURL(objectUrl);
        })
        .catch(error => {
            console.error('Error downloading file:', error);
            if (error.message.includes('401') || error.message.includes('403')) {
                navigate('/login');
            }
        });
    };

    // Очистка URL при размонтировании компонента
    useEffect(() => {
        return () => {
            Object.values(imageUrls).forEach(url => {
                URL.revokeObjectURL(url);
            });
        };
    }, [imageUrls]);

    const renderItemContent = (item: DocumentationItem) => {
        switch (item.type) {
            case DocumentationItemType.TEXT:
                return (
                    <Box sx={{ mt: 2 }}>
                        <div dangerouslySetInnerHTML={{ __html: item.content || '' }} />
                    </Box>
                );
            case DocumentationItemType.IMAGE:
                if (item.file_path && !imageUrls[item.file_path]) {
                    loadImage(item.file_path);
                }
                return (
                    <Box sx={{ mt: 2 }}>
                        {item.file_path && imageUrls[item.file_path] && (
                            <>
                                <img
                                    src={imageUrls[item.file_path]}
                                    alt={item.title || item.description || ''}
                                    style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                    }}
                                />
                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component="a"
                                        href="#"
                                        onClick={(e) => handleFileClick(e, getFileUrl(item.file_path), item.title || (item.file_path ? item.file_path.split('/').pop() : undefined))}
                                        startIcon={<DownloadIcon />}
                                    >
                                        Download Image
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                );
            case DocumentationItemType.FILE:
                const fileUrl = getFileUrl(item.file_path);
                return (
                    <Box sx={{ mt: 2 }}>
                        {fileUrl && (
                            <Button
                                variant="outlined"
                                component="a"
                                href="#"
                                onClick={(e) => handleFileClick(e, fileUrl, item.title || (item.file_path ? item.file_path.split('/').pop() : undefined))}
                                startIcon={<DownloadIcon />}
                            >
                                Download {item.title || 'File'}
                            </Button>
                        )}
                    </Box>
                );
            case DocumentationItemType.CODE:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{item.content}</pre>
                        </Paper>
                    </Box>
                );
            case DocumentationItemType.LINK:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            component="a"
                            href={item.content || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon />}
                        >
                            Open Link
                        </Button>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {documentation && (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                        <Typography variant="h4" component="h1">
                            {documentation.title}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenDialog(true)}
                        >
                            Add Item
                        </Button>
                    </Box>

                    <Typography color="textSecondary" paragraph>
                        {documentation.description}
                    </Typography>

                    <Grid container spacing={3}>
                        {documentation.items.map((item) => (
                            <Grid item xs={12} key={item.id}>
                                <Paper sx={{ p: 3 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h6">
                                            {item.title || item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                                        </Typography>
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setEditingItem(item);
                                                    setNewItemContent(item.content || '');
                                                    setNewItemDescription(item.description || '');
                                                    setNewItemTitle(item.title || '');
                                                    setOpenDialog(true);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteItem(item.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {item.description && (
                                        <Typography color="textSecondary" paragraph>
                                            {item.description}
                                        </Typography>
                                    )}
                                    {renderItemContent(item)}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Dialog
                        open={openDialog}
                        onClose={() => {
                            setOpenDialog(false);
                            resetForm();
                        }}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            {editingItem ? 'Edit Item' : 'Add New Item'}
                        </DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Title"
                                value={newItemTitle}
                                onChange={(e) => setNewItemTitle(e.target.value)}
                                required
                            />

                            {!editingItem && (
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Item Type</InputLabel>
                                    <Select
                                        value={newItemType}
                                        onChange={(e) => setNewItemType(e.target.value as DocumentationItemType)}
                                    >
                                        {Object.values(DocumentationItemType).map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type.charAt(0).toUpperCase() + type.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {newItemType === DocumentationItemType.TEXT && (
                                <Box sx={{ mt: 2 }}>
                                    <RichTextEditor
                                        value={newItemContent}
                                        onChange={setNewItemContent}
                                        readOnly={!!editingItem}
                                    />
                                </Box>
                            )}

                            {newItemType === DocumentationItemType.IMAGE && !editingItem && (
                                <Box sx={{ mt: 2 }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setSelectedFile(file);
                                                if (previewUrl) {
                                                    URL.revokeObjectURL(previewUrl);
                                                }
                                                const url = URL.createObjectURL(file);
                                                setPreviewUrl(url);
                                            }
                                        }}
                                    />
                                    {previewUrl && (
                                        <Box sx={{ mt: 2 }}>
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {newItemType === DocumentationItemType.FILE && !editingItem && (
                                <Box sx={{ mt: 2 }}>
                                    <input
                                        type="file"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setSelectedFile(file);
                                                setNewItemContent(file.name);
                                            }
                                        }}
                                    />
                                    {selectedFile && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Selected file: {selectedFile.name}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {newItemType === DocumentationItemType.CODE && (
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={8}
                                        label="Code"
                                        value={newItemContent}
                                        onChange={(e) => setNewItemContent(e.target.value)}
                                        sx={{ fontFamily: 'monospace' }}
                                    />
                                </Box>
                            )}

                            {newItemType === DocumentationItemType.LINK && (
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="URL"
                                        value={newItemContent}
                                        onChange={(e) => setNewItemContent(e.target.value)}
                                        placeholder="https://"
                                    />
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                margin="normal"
                                label="Description"
                                value={newItemDescription}
                                onChange={(e) => setNewItemDescription(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setOpenDialog(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={editingItem ? handleUpdateItem : handleCreateItem}
                                color="primary"
                                disabled={
                                    !newItemTitle ||
                                    (!editingItem && 
                                        ((newItemType === DocumentationItemType.FILE || newItemType === DocumentationItemType.IMAGE) && !selectedFile) ||
                                        (newItemType !== DocumentationItemType.FILE && newItemType !== DocumentationItemType.IMAGE && !newItemContent))
                                }
                            >
                                {editingItem ? 'Update' : 'Create'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Container>
    );
};

export default DocumentationView; 